import { supabase } from '../lib/supabase';
import {
  getSubscriberOrderColumn,
  getSubscriberSignedAt
} from '../utils/subscriberFields';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfUtcDay(d) {
  const x = new Date(d);
  return new Date(
    Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate())
  );
}

function lastNDaysKeys(n) {
  const today = startOfUtcDay(new Date());
  const keys = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const t = new Date(today.getTime() - i * DAY_MS);
    keys.push(t.toISOString().slice(0, 10));
  }
  return keys;
}

export const MAX_HOMEPAGE_SLIDER_ITEMS = 10;

/**
 * Homepage publish flags (single row id=1). Public site reads these; staff updates via Publish.
 */
export async function fetchHomepagePublishSettings() {
  const { data, error } = await supabase
    .from('site_homepage_settings')
    .select(
      'id, slider_published, categories_published, slider_published_at, categories_published_at, updated_at'
    )
    .eq('id', 1)
    .maybeSingle();

  const defaults = {
    slider_published: false,
    categories_published: false,
    slider_published_at: null,
    categories_published_at: null
  };

  if (error) {
    return {
      settings: { ...defaults },
      error
    };
  }

  return {
    settings: data ? { ...defaults, ...data } : defaults,
    error: null
  };
}

export async function publishHomepageSlider() {
  const now = new Date().toISOString();
  const { settings } = await fetchHomepagePublishSettings();
  const { data, error } = await supabase
    .from('site_homepage_settings')
    .upsert(
      {
        id: 1,
        slider_published: true,
        slider_published_at: now,
        categories_published:
          settings.categories_published !== false,
        categories_published_at: settings.categories_published_at || null,
        updated_at: now
      },
      { onConflict: 'id' }
    )
    .select()
    .maybeSingle();
  return { data, error };
}

export async function publishHomepageCategories() {
  const now = new Date().toISOString();
  const { settings } = await fetchHomepagePublishSettings();
  const { data, error } = await supabase
    .from('site_homepage_settings')
    .upsert(
      {
        id: 1,
        categories_published: true,
        categories_published_at: now,
        slider_published: settings.slider_published !== false,
        slider_published_at: settings.slider_published_at || null,
        updated_at: now
      },
      { onConflict: 'id' }
    )
    .select()
    .maybeSingle();
  return { data, error };
}

export async function fetchDashboardOverview() {
  // Use published_date for "recent" — many DBs synced before created_at existed.
  const [moviesCount, channelsRes, categoriesCountRes, subsRes, recentMoviesRes, topViewedRes] =
    await Promise.all([
      supabase.from('movies').select('video_id', { count: 'exact', head: true }),
      supabase.from('movies').select('channel_name'),
      supabase.from('categories').select('id', { count: 'exact', head: true }),
      supabase.from('subscribers').select('is_active, source, created_at'),
      supabase
        .from('movies')
        .select('video_id, title, channel_name, published_date, view_count')
        .order('published_date', { ascending: false })
        .limit(8),
      supabase
        .from('movies')
        .select('video_id, title, view_count, channel_name')
        .order('view_count', { ascending: false })
        .limit(10)
    ]);

  const channels = new Set(
    (channelsRes.data || []).map((r) => r.channel_name).filter(Boolean)
  );

  const subs = subsRes.data || [];
  const activeSubscribers = subs.filter((s) => s.is_active).length;
  const inactiveSubscribers = subs.length - activeSubscribers;

  const signupByDay = {};
  lastNDaysKeys(30).forEach((k) => {
    signupByDay[k] = 0;
  });
  subs.forEach((s) => {
    const at = getSubscriberSignedAt(s);
    if (!at) return;
    const key = startOfUtcDay(new Date(at)).toISOString().slice(0, 10);
    if (signupByDay[key] !== undefined) signupByDay[key] += 1;
  });

  const errors = {
    movies: moviesCount.error,
    channels: channelsRes.error,
    categories: categoriesCountRes.error,
    subs: subsRes.error,
    recent: recentMoviesRes.error,
    top: topViewedRes.error
  };
  const errorSummary = Object.entries(errors)
    .filter(([, e]) => e)
    .map(([key, e]) => `${key}: ${e.message || e.details || e.hint || 'request failed'}`)
    .join(' · ');

  return {
    movieCount: moviesCount.count ?? 0,
    channelCount: channels.size,
    categoryCount: categoriesCountRes.count ?? 0,
    subscriberTotal: subs.length,
    subscriberActive: activeSubscribers,
    subscriberInactive: inactiveSubscribers,
    signupSeries: lastNDaysKeys(30).map((k) => ({ date: k, count: signupByDay[k] })),
    recentMovies: recentMoviesRes.data || [],
    topViewed: topViewedRes.data || [],
    errors,
    errorSummary: errorSummary || null
  };
}

export async function fetchAnalyticsPage() {
  const overview = await fetchDashboardOverview();
  return {
    ...overview,
    signupSeries30: overview.signupSeries.map(({ date, count }) => ({
      date,
      signups: count
    }))
  };
}

/**
 * Paginated movies for admin table.
 */
export async function fetchMoviesAdmin({
  page = 1,
  pageSize = 20,
  search = '',
  channel = '',
  sortField = 'published_date',
  sortAsc = false
}) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase.from('movies').select('*', { count: 'exact' });

  const term = search.trim();
  if (term) {
    const esc = term.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
    const p = `%${esc}%`;
    q = q.or(`title.ilike.${p},description.ilike.${p},channel_name.ilike.${p}`);
  }
  if (channel.trim()) {
    q = q.ilike('channel_name', `%${channel.trim()}%`);
  }

  const allowedSort = new Set([
    'title',
    'view_count',
    'published_date',
    'created_at',
    'channel_name'
  ]);
  const col = allowedSort.has(sortField) ? sortField : 'published_date';
  q = q.order(col, { ascending: sortAsc });

  const { data, error, count } = await q.range(from, to);

  return {
    rows: data || [],
    total: count ?? 0,
    page,
    pageSize,
    error
  };
}

export async function insertMovie(row) {
  const { data, error } = await supabase.from('movies').insert([row]).select();
  return { data: data?.[0], error };
}

export async function updateMovie(videoId, patch) {
  const { data, error } = await supabase
    .from('movies')
    .update(patch)
    .eq('video_id', videoId)
    .select();
  return { data: data?.[0], error };
}

export async function deleteMoviesByVideoIds(videoIds) {
  if (!videoIds.length) return { error: null };
  const { error } = await supabase.from('movies').delete().in('video_id', videoIds);
  return { error };
}

export async function fetchDistinctChannelNames() {
  const { data, error } = await supabase.from('movies').select('channel_name');
  const set = new Set((data || []).map((r) => r.channel_name).filter(Boolean));
  return { channels: [...set].sort((a, b) => a.localeCompare(b)), error };
}

function categoryMovieCount(row) {
  const agg = row?.movie_categories;
  if (Array.isArray(agg) && agg.length > 0 && agg[0]?.count != null) {
    return Number(agg[0].count) || 0;
  }
  return 0;
}

/**
 * Categories for admin (includes movie count via nested aggregate).
 */
export async function fetchCategoriesAdmin() {
  const { data, error } = await supabase
    .from('categories')
    .select('id, name, slug, color, created_at, updated_at, movie_categories(count)')
    .order('name', { ascending: true });

  const rows = (data || []).map((r) => ({
    ...r,
    movie_count: categoryMovieCount(r)
  }));

  return { rows, error };
}

export async function insertCategory(row) {
  const { data, error } = await supabase.from('categories').insert([row]).select();
  return { data: data?.[0], error };
}

export async function updateCategory(id, patch) {
  const { data, error } = await supabase
    .from('categories')
    .update(patch)
    .eq('id', id)
    .select();
  return { data: data?.[0], error };
}

export async function deleteCategory(id) {
  const { error } = await supabase.from('categories').delete().eq('id', id);
  return { error };
}

export async function fetchMovieCategoryIds(videoId) {
  const { data, error } = await supabase
    .from('movie_categories')
    .select('category_id')
    .eq('video_id', videoId);
  const ids = (data || []).map((r) => r.category_id);
  return { categoryIds: ids, error };
}

export async function setMovieCategories(videoId, categoryIds) {
  const { error: delErr } = await supabase
    .from('movie_categories')
    .delete()
    .eq('video_id', videoId);
  if (delErr) return { error: delErr };

  const unique = [...new Set((categoryIds || []).filter(Boolean))];
  if (unique.length === 0) return { error: null };

  const rows = unique.map((category_id) => ({ video_id: videoId, category_id }));
  const { error } = await supabase.from('movie_categories').insert(rows);
  return { error };
}

/**
 * Movies currently linked to a collection (admin).
 */
export async function fetchMoviesForCategory(categoryId) {
  const { data, error } = await supabase
    .from('movie_categories')
    .select('video_id, movies ( video_id, title )')
    .eq('category_id', categoryId);

  if (error) return { rows: [], error };

  const rows = (data || []).map((r) => ({
    video_id: r.video_id,
    title: r.movies?.title || r.video_id
  }));
  return { rows, error: null };
}

/**
 * Set which movies belong to this collection; preserves other collections per movie.
 */
export async function setCollectionMembership(categoryId, videoIds) {
  const desired = [...new Set((videoIds || []).filter(Boolean))];

  const { data: currentRows, error: curErr } = await supabase
    .from('movie_categories')
    .select('video_id')
    .eq('category_id', categoryId);
  if (curErr) return { error: curErr };

  const current = new Set((currentRows || []).map((r) => r.video_id));

  for (const videoId of current) {
    if (!desired.includes(videoId)) {
      const { categoryIds, error: fErr } = await fetchMovieCategoryIds(videoId);
      if (fErr) return { error: fErr };
      const next = categoryIds.filter((id) => String(id) !== String(categoryId));
      const { error } = await setMovieCategories(videoId, next);
      if (error) return { error };
    }
  }

  for (const videoId of desired) {
    if (!current.has(videoId)) {
      const { categoryIds, error: fErr } = await fetchMovieCategoryIds(videoId);
      if (fErr) return { error: fErr };
      if (categoryIds.some((id) => String(id) === String(categoryId))) continue;
      const { error } = await setMovieCategories(videoId, [
        ...categoryIds,
        categoryId
      ]);
      if (error) return { error };
    }
  }

  return { error: null };
}

/**
 * Homepage slider rows joined with movie metadata (ordered by sort_order).
 */
export async function fetchSliderItemsAdmin() {
  const { data, error } = await supabase
    .from('homepage_slider_items')
    .select(
      `
      sort_order,
      video_id,
      movies (
        video_id,
        title,
        channel_name,
        published_date,
        description,
        view_count,
        duration,
        embed_code
      )
    `
    )
    .order('sort_order', { ascending: true });

  const rows = (data || []).map((r) => ({
    sort_order: r.sort_order,
    video_id: r.video_id,
    movie: r.movies || null
  }));

  return { rows, error };
}

async function renumberSliderSortOrders(orderedVideoIds) {
  const n = orderedVideoIds.length;
  const base = 100000;
  for (let i = 0; i < n; i += 1) {
    const { error } = await supabase
      .from('homepage_slider_items')
      .update({ sort_order: base + i })
      .eq('video_id', orderedVideoIds[i]);
    if (error) return { error };
  }
  for (let i = 0; i < n; i += 1) {
    const { error } = await supabase
      .from('homepage_slider_items')
      .update({ sort_order: i + 1 })
      .eq('video_id', orderedVideoIds[i]);
    if (error) return { error };
  }
  return { error: null };
}

export async function addSliderMovie(videoId) {
  const { count, error: countErr } = await supabase
    .from('homepage_slider_items')
    .select('video_id', { count: 'exact', head: true });
  if (countErr) return { error: countErr };
  if ((count ?? 0) >= MAX_HOMEPAGE_SLIDER_ITEMS) {
    return {
      error: new Error(
        `Homepage slider is limited to ${MAX_HOMEPAGE_SLIDER_ITEMS} movies.`
      )
    };
  }

  const { data: maxRow, error: maxErr } = await supabase
    .from('homepage_slider_items')
    .select('sort_order')
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (maxErr) return { error: maxErr };

  const nextOrder = (maxRow?.sort_order ?? 0) + 1;
  const { error } = await supabase
    .from('homepage_slider_items')
    .insert({ video_id: videoId, sort_order: nextOrder });
  return { error };
}

export async function removeSliderMovie(videoId) {
  const { data: rows, error: listErr } = await supabase
    .from('homepage_slider_items')
    .select('video_id, sort_order')
    .order('sort_order', { ascending: true });
  if (listErr) return { error: listErr };

  const ordered = (rows || []).map((r) => r.video_id).filter(Boolean);
  const next = ordered.filter((id) => id !== videoId);
  if (next.length === ordered.length) return { error: null };

  const { error: delErr } = await supabase
    .from('homepage_slider_items')
    .delete()
    .eq('video_id', videoId);
  if (delErr) return { error: delErr };

  return renumberSliderSortOrders(next);
}

export async function moveSliderMovie(videoId, direction) {
  const { data: rows, error: listErr } = await supabase
    .from('homepage_slider_items')
    .select('video_id, sort_order')
    .order('sort_order', { ascending: true });
  if (listErr) return { error: listErr };

  const ordered = (rows || []).map((r) => r.video_id);
  const idx = ordered.indexOf(videoId);
  if (idx < 0) return { error: null };
  const j = direction === 'up' ? idx - 1 : idx + 1;
  if (j < 0 || j >= ordered.length) return { error: null };

  const next = [...ordered];
  [next[idx], next[j]] = [next[j], next[idx]];
  return renumberSliderSortOrders(next);
}

export async function fetchSubscribersAdmin({
  page = 1,
  pageSize = 25,
  search = '',
  activeOnly = false
}) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase.from('subscribers').select('*', { count: 'exact' });

  if (activeOnly) {
    q = q.eq('is_active', true);
  }

  const term = search.trim().toLowerCase();
  if (term) {
    q = q.ilike('email', `%${term}%`);
  }

  q = q.order(getSubscriberOrderColumn(), { ascending: false });

  const { data, error, count } = await q.range(from, to);

  return {
    rows: data || [],
    total: count ?? 0,
    page,
    pageSize,
    error
  };
}
